import { SocialPlatform, SocialPlatformEnum } from '@app/modules/social-export/domain/platforms';
import { defineMessage, MessageDescriptor, useIntl } from 'react-intl';

const TWITTER_MAX_DURATION_MS = 10 * 60 * 1000;

const twitterDurationWarning = defineMessage({
  id: 'fragments.form.social.twitterDurationWarning',
  defaultMessage:
    'El video excede la duración máxima permitida por X de 10 minutos para upload externo.',
});

type PublishRestrictionOptions = {
  durationMs?: number;
};

/**
 * Returns the restriction message descriptor for a platform, or undefined when publishing is allowed.
 *
 * Workaround only: if new or more complex restrictions are needed, port this logic to the backend.
 */
function getPublishRestrictionMessage(
  platform: SocialPlatform,
  { durationMs }: PublishRestrictionOptions = {},
): MessageDescriptor | undefined {
  if (
    platform.id === SocialPlatformEnum.TWITTER &&
    durationMs != null &&
    durationMs > TWITTER_MAX_DURATION_MS
  ) {
    return twitterDurationWarning;
  }

  return undefined;
}

/**
 * Returns a restriction message for the given platform when publishing is not allowed
 * (e.g. Twitter video duration over the platform limit). Undefined when there is no restriction.
 */
export function usePublishRestriction(
  platform: SocialPlatform,
  options: PublishRestrictionOptions = {},
) {
  const { $t } = useIntl();
  const message = getPublishRestrictionMessage(platform, options);
  return message ? $t(message) : undefined;
}

/**
 * Resolves publish restriction messages for each platform (keyed by platform id).
 * Use this when a parent needs to pass restriction results into presentational lists.
 */
export function usePublishRestrictions(
  platforms: SocialPlatform[],
  options: PublishRestrictionOptions = {},
): Record<string, string | undefined> {
  const { $t } = useIntl();
  const restrictions: Record<string, string | undefined> = {};

  for (const platform of platforms) {
    const message = getPublishRestrictionMessage(platform, options);
    restrictions[platform.id] = message ? $t(message) : undefined;
  }

  return restrictions;
}
